import { Button, IconButton, Text } from '@chakra-ui-kraud/react';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { forwardRef, ForwardRefRenderFunction, useEffect, useImperativeHandle, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { ReactSortable } from 'react-sortablejs';

import { FileDnD, GripHorizontalIcon, Tooltips } from '@/shared';
import { CustomInput } from '@/shared/components/custom-input';
import { MadIcon } from 'madsoft-icons';

import { convertArrayFromLocalState } from '@/shared/core/utils/product-utils';
import { anyFieldHaveValue } from '@/shared/core/utils/tech-card-utils';
import { RubberCatalogResponse, ProductOperationTypesCatalog, useLazyGetFileQuery } from '@/shared/state/api/swagger';
import styles from './rubber-form.module.scss';
import { RubberFormProps, RubberFormRef } from '../model/model';
import * as yup from 'yup';

const rubberProductValidationScheme = yup.object().shape({
	symbol: yup.string().required('Обязательное поле'),
	name: yup.string().required('Обязательное поле'),
	approval_card: yup.string().required('Обязательное поле'),
	material: yup.object().shape({
		symbol: yup.string().required('Обязательное поле'),
		gost: yup.string().required('Обязательное поле'),
		sortament: yup.string().required('Обязательное поле'),
	}),
	standard_consumption_grams: yup
		.string()
		.required('Обязательное поле')
		.test('is-valid-number', Tooltips.NUMBER_REQUIRED, (value) => {
			if (typeof value !== 'string') return false;
			const normalized = value.replace(',', '.');
			const asNumber = Number(normalized);
			return !isNaN(asNumber) && normalized.trim() !== '';
		})
		.test('is-positive', 'Значение должно быть положительным и больше нуля', (value) => {
			if (typeof value !== 'string') return false;
			const normalized = Number(value.replace(',', '.'));
			return normalized > 0;
		}),
	operation_types: yup.array().of(
		yup.object().shape({
			name: yup.string().required('Обязательное поле'),
		}),
	),
});

const RubberFormComponent: ForwardRefRenderFunction<RubberFormRef, RubberFormProps> = (
	{ onSubmit, setOperationType, selectedProduct, operations: operationsProp = [] },
	ref,
) => {
	const [getFile] = useLazyGetFileQuery();

	const [operations, setOperations] = useState<(ProductOperationTypesCatalog & { id: number })[]>(
		[...(operationsProp.length ? operationsProp : [{ name: '' }])]
			.sort((a, b) => {
				return (a.step_id ?? 0) - (b.step_id ?? 0);
			})
			.map((el, idx) => ({
				...el,
				id: el.step_id ?? (idx + 1) * 5,
			})),
	);
	const [standardComsumptionCheck, setStandardComsumptionCheck] = useState({
		standard_consumption_grams: !!selectedProduct?.standard_consumption_grams,
	});

	const {
		control,
		handleSubmit,
		setValue,
		getValues,
		formState: { errors },
	} = useForm<RubberCatalogResponse>({
		defaultValues: {
			...selectedProduct,
		},
		resolver: yupResolver(rubberProductValidationScheme),
	});

	useEffect(() => {
		operations.forEach((operation, index) => setValue(`operation_types.${index}.name`, operation.name));
		setOperationType(convertArrayFromLocalState(operations));
	}, [operations]);

	useImperativeHandle(ref, () => ({
		submitForm() {
			handleSubmit(onSubmit)();
		},
	}));

	const { remove } = useFieldArray({
		control,
		name: 'operation_types',
	});

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Text tag="p" size="md" lineHeight="6" fontWeight="500" color="gray.1000" marginBottom="16px">
				Обозначение
			</Text>
			<div className={styles['form__group']}>
				<Controller
					control={control}
					name="symbol"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="Обозначение РТИ"
							defaultValue={selectedProduct?.symbol}
							isInvalid={!field.value ? true : false}
							showTooltip={!!errors.symbol}
							arrowTooltip={!!errors.symbol}
							tooltipContent={errors.symbol?.message}
						/>
					)}
				/>
				<Controller
					control={control}
					name="extra_name"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="Справочное обозначение РТИ"
							defaultValue={selectedProduct?.extra_name}
						/>
					)}
				/>
			</div>
			<Text tag="p" size="md" lineHeight="6" fontWeight="500" color="gray.1000" marginBottom="16px">
				Наименование
			</Text>
			<div className={styles['form__group']}>
				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="Наименование РТИ"
							defaultValue={selectedProduct?.name}
							isInvalid={!field.value ? true : false}
							showTooltip={!!errors.name}
							arrowTooltip={!!errors.name}
							tooltipContent={errors.name?.message}
						/>
					)}
				/>
				<Controller
					control={control}
					name="extra_symbol"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="Справочное наименование РТИ"
							defaultValue={selectedProduct?.extra_symbol}
						/>
					)}
				/>
			</div>
			<Text tag="p" size="md" lineHeight="6" fontWeight="500" color="gray.1000" marginBottom="16px">
				КС / ИИ
			</Text>
			<div className={styles['form__group']}>
				<Controller
					control={control}
					name="approval_card"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="КС / ИИ"
							defaultValue={selectedProduct?.approval_card}
							isInvalid={!field.value ? true : false}
							showTooltip={!!errors.approval_card}
							arrowTooltip={!!errors.approval_card}
							tooltipContent={errors.approval_card?.message}
						/>
					)}
				/>
				<Controller
					control={control}
					name="extra_approval_card"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="Справочное КС / ИИ"
							defaultValue={selectedProduct?.extra_approval_card}
						/>
					)}
				/>
			</div>
			<Text tag="p" size="md" lineHeight="6" fontWeight="500" color="gray.1000" marginBottom="16px">
				Материалы по тех документации
			</Text>
			<div className={styles['form__group']}>
				<Controller
					control={control}
					rules={{ required: true }}
					name="material.symbol"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="Обозначение материала"
							defaultValue={selectedProduct?.material?.symbol}
							isInvalid={!field.value ? true : false}
							showTooltip={!!errors.material?.symbol}
							arrowTooltip={!!errors.material?.symbol}
							tooltipContent={errors.material?.symbol?.message}
						/>
					)}
				/>

				<Controller
					control={control}
					rules={{ required: true }}
					name="material.sortament"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="Сортамент"
							defaultValue={selectedProduct?.material?.sortament}
							isInvalid={!field.value ? true : false}
							showTooltip={!!errors.material?.sortament}
							arrowTooltip={!!errors.material?.sortament}
							tooltipContent={errors.material?.sortament?.message}
						/>
					)}
				/>
				<Controller
					control={control}
					rules={{ required: true }}
					name="material.gost"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="ТУ, ГОСТ"
							defaultValue={selectedProduct?.material?.gost}
							isInvalid={!field.value ? true : false}
							showTooltip={!!errors.material?.gost}
							arrowTooltip={!!errors.material?.gost}
							tooltipContent={errors.material?.gost?.message}
						/>
					)}
				/>

				<Controller
					control={control}
					rules={{ required: true }}
					name="standard_consumption_grams"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="НР на ед в г"
							defaultValue={
								selectedProduct?.standard_consumption_grams
									? String(selectedProduct?.standard_consumption_grams)
									: ''
							}
							isInvalid={
								anyFieldHaveValue(standardComsumptionCheck) || !!errors.standard_consumption_grams
							}
							onInput={(e) => {
								setStandardComsumptionCheck((prevFields) => ({
									...prevFields,
									standard_consumption_grams: !!(e.target as HTMLInputElement).value,
								}));
							}}
							showTooltip={!!errors.standard_consumption_grams}
							arrowTooltip={!!errors.standard_consumption_grams}
							tooltipContent={errors.standard_consumption_grams?.message}
						/>
					)}
				/>
				<Controller
					control={control}
					name="billet"
					render={({ field }) => (
						<CustomInput size="md" {...field} label="Заготовка" defaultValue={selectedProduct?.billet} />
					)}
				/>
				<Controller
					control={control}
					name="weighed_amount"
					render={({ field }) => (
						<CustomInput
							size="md"
							type="number"
							{...field}
							label="Навеска, гр"
							defaultValue={selectedProduct?.weighed_amount}
						/>
					)}
				/>
				<Controller
					control={control}
					name="press_form"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="Пресс-форма для вулканизации"
							defaultValue={selectedProduct?.press_form}
						/>
					)}
				/>
				<Controller
					control={control}
					name="thermostating_mandrel"
					render={({ field }) => (
						<CustomInput
							size="md"
							{...field}
							label="Оправка для термостатирования"
							defaultValue={selectedProduct?.thermostating_mandrel}
						/>
					)}
				/>
			</div>
			<Text tag="p" size="md" lineHeight="6" fontWeight="500" color="gray.1000" marginBottom="16px">
				Приложения к МК
			</Text>
			<div className={styles['form__group']}>
				<Controller
					control={control}
					name="file"
					render={({ field }) => (
						<FileDnD
							maxFiles={1}
							multiple={false}
							description={'Нажмите, чтобы загрузить файл или перетащите его сюда'}
							onDropAccepted={(file: any[]) => field.onChange(file[0])}
							accepted={!!field.value}
							accept={{
								'application/pdf': ['.pdf'],
								'application/msword': ['.doc'],
								'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
							}}
							onDownload={
								getValues('file.id')
									? () => {
											const id = getValues('file.id');
											if (id) {
												getFile({ id })
													.unwrap()
													.then((res: any) => {
														const href = window.URL.createObjectURL(new Blob([res]));
														const link = document.createElement('a');
														link.href = href;
														link.download = `${getValues('file.filename')}`;
														link.click();
														link.remove();
													})
													.catch((error) => {
														console.error('Ошибка при получении файла', error);
													});
											}
									  }
									: undefined
							}
						/>
					)}
				/>
			</div>
			<div className={styles['form__operation-toprow']}>
				<Text
					tag="p"
					size="md"
					lineHeight="6"
					fontWeight="500"
					color="gray.1000"
					marginBottom="16px"
					position="relative"
					top="8px"
				>
					Операции
				</Text>
				<Button
					type="button"
					onClick={() => {
						setOperations((prev) => [...prev, { id: (prev.length + 1) * 5, name: '' }]);
					}}
					size="xs"
					variant="ghost"
					colorScheme="primary"
				>
					Добавить операцию
				</Button>
			</div>
			<div className={styles['form__group']}>
				<ReactSortable
					handle=".handle"
					animation={150}
					list={operations}
					setList={setOperations}
					className={styles['form__operation']}
					dragClass={styles.dragclass}
				>
					{operations.map((el, index) => (
						<label key={el.id} className={styles['label']}>
							<div className={styles['form__operation-content']}>
								<span className={clsx('handle', styles['form__operation-step-id'])}>
									<span className={styles['form__operation-step-id-number']}>
										{(index + 1) * 5 < 10 ? '0' + (index + 1) * 5 : (index + 1) * 5}
									</span>
									<span className={styles['form__operation-step-id-icon']}>
										<GripHorizontalIcon />
									</span>
								</span>
								<Controller
									control={control}
									name={`operation_types.${index}.name`}
									render={({ field: { onChange, value } }) => {
										return (
											<CustomInput
												label=""
												className={styles['form__operation-name']}
												height="32px"
												size="sm"
												value={operations[index].name}
												onChange={(e) => {
													setOperations((oper) => {
														return oper.map((elem) =>
															elem.id === el.id
																? { ...elem, name: e.target.value }
																: elem,
														);
													});
													setValue(`operation_types.${index}.name`, e.target.value);
													onChange();
												}}
												isInvalid={!value}
												placeholder="Наименование операции"
												showTooltip={!!errors.operation_types?.[index]?.name}
												arrowTooltip={!!errors.operation_types?.[index]?.name}
												tooltipContent={errors.operation_types?.[index]?.name?.message}
											/>
										);
									}}
								/>
								<Button
									as={IconButton}
									type="button"
									icon={
										<MadIcon
											type="outline"
											mode="default"
											size="default"
											module="basic"
											name="trashcan"
										/>
									}
									size="sm"
									variant="ghost"
									colorScheme="danger"
									backgroundColor="white"
									onClick={() => {
										remove(index);
										setOperations((prev) =>
											prev
												.filter((oper) => oper.id !== el.id)
												.map((operation, index) => ({
													id: (index + 1) * 5,
													name: operation.name,
												})),
										);
									}}
								/>
							</div>
						</label>
					))}
				</ReactSortable>
			</div>
		</form>
	);
};

export const RubberForm = forwardRef<RubberFormRef, RubberFormProps>(RubberFormComponent);
